import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useAuth } from '../../hooks/useAuth';
import { updateReportMetadata } from '../../services/reportService';
import showNotification from '../common/Notification';
import { computeReportRevenuePreview, formatTry } from '../../utils/reportRevenueUtils';

/**
 * AOV input + estimated monthly/yearly revenue cards
 */
const ReportRevenuePanel = ({ reportId, report, onUpdated }) => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const [aov, setAov] = useState(report?.averageOrderValue || 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAov(report?.averageOrderValue || 0);
  }, [report?.averageOrderValue]);

  const preview = computeReportRevenuePreview(report, Number(aov) || 0);
  const monthly = report?.estimatedMonthlyRevenue || preview.monthly;
  const yearly = report?.estimatedYearlyRevenue || preview.yearly;
  const displayMonthly = Number(aov) > 0 ? preview.monthly : monthly;
  const displayYearly = Number(aov) > 0 ? preview.yearly : yearly;

  const handleSave = useCallback(async () => {
    if (!reportId || !canEdit) return;
    try {
      setSaving(true);
      const updated = await updateReportMetadata(reportId, {
        averageOrderValue: Number(aov) || 0,
      });
      showNotification('AOV ve gelir tahmini kaydedildi', 'success');
      onUpdated?.(updated);
    } catch {
      showNotification('Kaydetme sırasında bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  }, [reportId, canEdit, aov, onUpdated]);

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, mb: 2, border: '1px solid #eee', borderRadius: 2, bgcolor: '#fafafa' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AttachMoneyIcon sx={{ color: '#240B4B' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#240B4B' }}>
          Gelir Etkisi (AOV)
        </Typography>
      </Box>

      {canEdit ? (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
          <TextField
            label="Ortalama Sipariş Değeri (₺)"
            type="number"
            size="small"
            value={aov}
            onChange={(e) => setAov(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            sx={{ width: 220 }}
          />
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: '#240B4B', '&:hover': { bgcolor: '#3d1c6b' }, mt: 0.25 }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Kaydet'}
          </Button>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          AOV: {formatTry(report?.averageOrderValue || 0)}
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e8f5e9', bgcolor: '#f1f8f4' }}>
            <Typography variant="caption" color="text.secondary">
              Tahmini Aylık Gelir
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#028C4E' }}>
              {formatTry(displayMonthly)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e3f2fd', bgcolor: '#f5f9ff' }}>
            <Typography variant="caption" color="text.secondary">
              Tahmini Yıllık Gelir
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#240B4B' }}>
              {formatTry(displayYearly)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {!Number(aov) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          AOV girildiğinde kazanan varyasyonun tam trafiğe uygulanması senaryosuna göre gelir hesaplanır.
        </Typography>
      )}
    </Paper>
  );
};

export default ReportRevenuePanel;
